<script setup lang="ts">
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import LoginStepWrapper from '../LoginStepWrapper.vue';
import { Gender } from '@store/form';
import MrInput from '@components/base/mr-input/MrInput.vue';

const { mobile, token } = defineProps<{
    mobile: string;
    token: string;
}>();

const emit = defineEmits<{
    back: [];
    submit: [];
}>();

const authStore = useAuthStore();
const { loading, error } = classToRefs(authStore);
const analyticsStore = useAnalyticsStore();
const input = ref<InstanceType<typeof MrInput>>();
const gtm = useGtm();
const signupFName = ref('');
const signupLName = ref('');
const signupPassword = ref('');
const signupEmail = ref('');
const showPassword = ref(false);
const gender = ref<Gender>(Gender.None);
const genderOptions = [
    {
        label: 'آقا',
        value: Gender.Male,
    },
    {
        label: 'خانم',
        value: Gender.Female,
    },
];
const male = computed(() => {
    if (gender.value === Gender.None) {
        return undefined;
    }
    return gender.value === Gender.Male;
});
const disabled = computed(
    () =>
        !!error.value.FirstName ||
        !!error.value.LastName ||
        !!error.value.Email ||
        !!error.value.Password ||
        !!error.value.Gender ||
        !signupFName.value ||
        !signupLName.value ||
        !signupPassword.value ||
        gender.value === 2
);
const submitForm = async () => {
    analyticsStore.trackAll('signup_submit');
    try {
        await authStore.signup({
            firstName: signupFName.value,
            lastName: signupLName.value,
            password: signupPassword.value,
            email: signupEmail.value,
            male: male.value,
            token,
            mobile,
        });
        analyticsStore.trackAll('signup_result', { status: 'success' });
        analyticsStore.trackAll('signUp');
        analyticsStore.trackAll('logIn');
        gtm?.trackEvent({
            event: 'signup confirm button',
        });
        emit('submit');
    } catch (e) {
        analyticsStore.trackAll('signup_result', { status: 'failed' });
        reportException(e);
    }
};
watch(
    [signupFName, signupLName, signupPassword, signupEmail, gender],
    ([firstName, lastName, password, email, gender]) => {
        authStore.signupValidation({
            password,
            firstName,
            lastName,
            email,
            gender,
            mobile,
        });
    }
);
onMounted(() => input.value?.focus());
</script>

<template>
    <LoginStepWrapper
        title="ثبت‌نام در مِستربلیط"
        sub-title="برای تایید ثبت‌نام، لطفا موارد زیر را تکمیل نمائید."
        @submit="submitForm"
    >
        <template #inputs>
            <mr-input-container
                size="dense"
                :invalid="!!error['Gender']"
                :description="error['Gender']"
                :hide-description="!error['Gender']"
                ghost
            >
                <mr-radio-button
                    v-model="gender"
                    :radios="genderOptions"
                    name="gender"
                    row
                    :gap-items="20"
                    :gap-items-mobile="4"
                />
            </mr-input-container>
            <input type="text" class="hidden" name="username" :value="mobile" />
            <mr-input
                ref="input"
                v-model="signupFName"
                type="text"
                placeholder="نام"
                :error-text="error['FirstName']"
                :invalid="!!error['FirstName']"
                :hide-description="!error['FirstName']"
                required
                @submit="submitForm"
            />
            <mr-input
                v-model="signupLName"
                type="text"
                placeholder="نام خانوادگی"
                :error-text="error['LastName']"
                :invalid="!!error['LastName']"
                :hide-description="!error['LastName']"
                required
                @submit="submitForm"
            />
            <mr-input
                v-model="signupEmail"
                dir="ltr"
                autocomplete="email"
                placeholder="ایمیل"
                :error-text="error['Email']"
                :invalid="!!error['Email']"
                :hide-description="!error['Email']"
                @submit="submitForm"
            />
            <mr-input
                v-model="signupPassword"
                :type="showPassword ? undefined : 'password'"
                :icon="showPassword ? faEyeSlash : faEye"
                dir="ltr"
                autocomplete="new-password"
                placeholder="رمز ورود"
                :error-text="error['Password']"
                :invalid="!!error['Password']"
                :hide-description="!error['Password']"
                required
                @submit="submitForm"
                @click:icon="showPassword = !showPassword"
            />
        </template>
        <template #actions>
            <mr-button
                id="login-btn-signup"
                full-width
                :loading
                type="submit"
                :disabled
            >
                تایید ثبت‌نام
            </mr-button>
        </template>
    </LoginStepWrapper>
</template>
<style lang="scss" scoped>
:deep(.input-container) {
    background-color: white !important;
    border-radius: 8px;
}
.hidden {
    display: none;
}
</style>
